/**
 * 公共 API 模块
 * 统一管理所有后端接口调用
 */

// 后端服务基础地址
const BASE_URL = 'http://124.222.3.177:8081'

/**
 * 尝试从响应中解析 JSON，失败则返回 null
 */
function tryParseJson(res) {
  return res.text().then(function (text) {
    try {
      return text ? JSON.parse(text) : null
    } catch (e) {
      return { _raw: text }
    }
  })
}

/**
 * 通用请求方法（全局错误拦截）
 * - HTTP 400：弹窗提示后端返回的错误消息 message
 * - 其他非 2xx：抛出带错误信息的 Error
 * @param {string} url 接口路径（以 / 开头）
 * @param {object} options fetch 配置项
 * @returns {Promise<any>} 返回 JSON 数据
 */
function request(url, options) {
  return fetch(BASE_URL + url, options).then(function (res) {
    if (res.ok) {
      return res.json()
    }
    // 非 2xx 响应，先读取 body 解析错误信息
    return tryParseJson(res).then(function (json) {
      var message = ''
      if (json) {
        message = json.message || json.msg || json.error || json._raw || ''
      }
      if (res.status === 400) {
        // 全局 400 拦截：弹窗显示后端给出的错误消息
        alert(message || ('请求错误（400）：' + url))
        var err400 = new Error(message || '请求错误（400）')
        err400.status = 400
        err400.data = json
        throw err400
      }
      var err = new Error(message || ('请求失败: ' + res.status))
      err.status = res.status
      err.data = json
      throw err
    })
  })
}

/**
 * 获取所有分类
 * GET /api/categories/all
 * @returns {Promise<{code: string, data: Array, message: string}>}
 */
export function getCategories() {
  return request('/api/categories/all')
}

/**
 * 获取全部助手
 * GET /api/assistants/all
 * @returns {Promise<{code: string, data: Array, message: string}>}
 */
export function getAllAssistants() {
  return request('/api/assistants/all')
}

/**
 * 按分类获取助手列表
 * GET /api/assistants/category/{categoryId}
 * @param {string|number} categoryId 分类ID
 * @returns {Promise<{code: string, data: Array, message: string}>}
 */
export function getAssistantsByCategory(categoryId) {
  return request('/api/assistants/category/' + categoryId)
}

/**
 * 生成标准 UUID v4（不依赖第三方库，不重复）
 * @returns {string} 如 "550e8400-e29b-41d4-a716-446655440000"
 */
function generateUUID() {
  // 优先使用浏览器原生 crypto.randomUUID（现代浏览器支持）
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID()
  }
  // 回退方案：基于 crypto.getRandomValues 的 RFC4122 v4 实现
  if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
    var arr = new Uint8Array(16)
    window.crypto.getRandomValues(arr)
    // 设置 version 和 variant 位
    arr[6] = (arr[6] & 0x0f) | 0x40  // version 4
    arr[8] = (arr[8] & 0x3f) | 0x80  // variant 10
    var hex = []
    for (var i = 0; i < 16; i++) {
      hex.push(arr[i].toString(16).padStart(2, '0'))
    }
    return hex[0] + hex[1] + hex[2] + hex[3] + '-' +
           hex[4] + hex[5] + '-' +
           hex[6] + hex[7] + '-' +
           hex[8] + hex[9] + '-' +
           hex[10] + hex[11] + hex[12] + hex[13] + hex[14] + hex[15]
  }
  // 最终回退：Math.random（极低概率重复）
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = Math.random() * 16 | 0
    var v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

/**
 * 获取或创建用户ID
 * 从 localStorage 读取，不存在则生成标准 UUID 并持久化
 * @returns {string} 用户ID
 */
export function getOrCreateUserId() {
  var STORAGE_KEY = 'ai-chat-userId'
  var userId = localStorage.getItem(STORAGE_KEY)
  if (!userId) {
    userId = generateUUID()
    localStorage.setItem(STORAGE_KEY, userId)
  }
  return userId
}

/**
 * 默认对话流式请求
 * POST /api/chat/default
 * @param {string} message 用户消息
 * @param {(chunk: string, fullText: string) => void} onChunk 每次收到数据块的回调
 * @param {boolean} webSearch 是否启用联网搜索
 * @returns {Promise<string>} 完整回复文本
 */
export function streamChatDefault(message, onChunk, webSearch) {
  var userId = getOrCreateUserId()
  var params = 'message=' + encodeURIComponent(message) +
    '&userId=' + encodeURIComponent(userId) +
    '&webSearch=' + (webSearch ? 'true' : 'false')
  return fetch(BASE_URL + '/api/chat/default', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    body: params
  }).then(function (res) {
    if (!res.ok) {
      throw new Error('请求失败: ' + res.status)
    }
    var reader = res.body.getReader()
    var decoder = new TextDecoder('utf-8')
    var fullText = ''

    function readChunk() {
      return reader.read().then(function (_ref) {
        var done = _ref.done
        var value = _ref.value
        if (done) return fullText
        var chunk = decoder.decode(value, { stream: true })
        fullText += chunk
        if (onChunk) onChunk(chunk, fullText)
        return readChunk()
      })
    }

    return readChunk()
  })
}

/**
 * 已登录用户聊天流式请求
 * POST /api/chat  Content-Type: application/json
 * @param {string|number} assistantId 助手 ID（userAssistantId）
 * @param {string} message 用户消息
 * @param {string|number} userId 已登录用户 ID
 * @param {string|number} id 当前话题的 sessionId（后端话题 ID，无则传空字符串）
 * @param {(chunk: string, fullText: string) => void} onChunk 每次收到数据块的回调
 * @param {boolean} webSearch 是否启用联网搜索
 * @returns {Promise<string>} 完整回复文本
 */
export function streamChatLoggedIn(assistantId, message, userId, id, onChunk, webSearch) {
  var bodyObj = {
    assistantId: assistantId || 1,
    message: message || '',
    userId: userId,
    id: id || ''
  }
  if (typeof webSearch === 'boolean') {
    bodyObj.webSearch = webSearch
  }
  return fetch(BASE_URL + '/api/chat', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(bodyObj)
  }).then(function (res) {
    if (!res.ok) {
      throw new Error('请求失败: ' + res.status)
    }
    var reader = res.body.getReader()
    var decoder = new TextDecoder('utf-8')
    var fullText = ''

    function readChunk() {
      return reader.read().then(function (_ref) {
        var done = _ref.done
        var value = _ref.value
        if (done) return fullText
        var chunk = decoder.decode(value, { stream: true })
        fullText += chunk
        if (onChunk) onChunk(chunk, fullText)
        return readChunk()
      })
    }

    return readChunk()
  })
}

/**
 * 发送邮箱验证码（注册 / 忘记密码通用）
 * POST /api/login/email/code
 * @param {string} email 邮箱
 * @returns {Promise<{code: string, message: string, data?: any}>}
 */
export function sendEmailCode(email) {
  return request('/api/login/email/code', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email: email })
  })
}

/**
 * 用户注册
 * POST /api/login/register
 * @param {object} payload { email, password, nickName, code }
 * @returns {Promise<{code: string, message: string, data?: any}>}
 */
export function registerUser(payload) {
  return request('/api/login/register', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      email: payload.email,
      password: payload.password,
      nickName: payload.nickName,
      code: payload.code
    })
  })
}

/**
 * 用户登录
 * POST /api/login/login
 * @param {string} email 邮箱
 * @param {string} password 密码
 * @returns {Promise<{code: string, message: string, data?: {nickName?: string, email?: string, token?: string}}>}
 */
export function loginUser(email, password) {
  return request('/api/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email: email, password: password })
  })
}

/**
 * 重置密码
 * POST /api/login/reset-password
 * @param {object} payload { email, code, password }
 * @returns {Promise<{code: string, message: string, data?: any}>}
 */
export function resetPassword(payload) {
  return request('/api/login/reset-password', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      email: payload.email,
      code: payload.code,
      password: payload.password
    })
  })
}

/**
 * 已登录用户添加助手（助手商店一键添加）
 * POST /api/user-assistants/user/{userId}
 * Content-Type: application/json
 * @param {string|number} userId 用户 ID
 * @param {object} payload { name: string, emoji: string, prompt: string }
 * @returns {Promise<{code: string, message: string, data?: any}>}
 *          data 通常包含 { id: number|string, ... }，其中 id 即 userAssistantId
 */
export function addUserAssistant(userId, payload) {
  return request('/api/user-assistants/user/' + encodeURIComponent(userId), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      name: payload.name || '',
      emoji: payload.emoji || '',
      prompt: payload.prompt || ''
    })
  })
}

/**
 * 已登录用户编辑助手（修改名称）
 * POST /api/user-assistants/user/{userId}/{id}?title=xxx
 * Content-Type: application/x-www-form-urlencoded
 * @param {string|number} userId 用户 ID
 * @param {string|number} id 助手 ID（userAssistantId）
 * @param {string} title 新的助手名称
 * @returns {Promise<{code: string, message: string, data?: any}>}
 */
export function editUserAssistant(userId, id, title) {
  var query = 'title=' + encodeURIComponent(title || '')
  return request('/api/user-assistants/user/' + encodeURIComponent(userId) + '/' + encodeURIComponent(id) + '?' + query, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    }
  })
}

/**
 * 已登录用户：删除某个已添加的助手
 * DELETE /api/user-assistants/user/{userId}/{id}
 * @param {string|number} userId 用户 ID
 * @param {string|number} id user-assistant 关联 ID（助手对象的 userAssistantId）
 * @returns {Promise<{code: string, message: string, data?: any}>}
 */
export function deleteUserAssistant(userId, id) {
  return request('/api/user-assistants/user/' + encodeURIComponent(userId) + '/' + encodeURIComponent(id), {
    method: 'DELETE'
  })
}

/**
 * 获取系统可用的大模型列表
 * GET /api/ai-llms/system
 * @returns {Promise<{
 *   code: string,
 *   message: string,
 *   data?: Array<{ id: number, name: string, llmCode: string, userId: number|null }>,
 *   timestamp?: number
 * }>}
 */
export function getSystemLlms() {
  return request('/api/ai-llms/system')
}

/**
 * 已登录用户：给某个 user-assistant 保存默认话题信息（选中该助手后切到话题 tab 时调用）
 * POST /api/user-message-sessions/user-assistant/{userAssistantId}
 * Content-Type: application/json
 * @param {string|number} userAssistantId 用户助手关联 ID（addUserAssistant 返回的 data.id）
 * @param {object} payload { title: string, userId: string|number }
 * @returns {Promise<{code: string, message: string, data?: any}>}
 */
export function saveDefaultTopicByUserAssistantId(userAssistantId, payload) {
  return request('/api/user-message-sessions/user-assistant/' + encodeURIComponent(userAssistantId), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      title: payload.title || '',
      userId: payload.userId
    })
  })
}

/**
 * 获取某个 user-assistant 下的话题列表
 * GET /api/user-message-sessions/user-assistant/{userAssistantId}
 * @param {string|number} userAssistantId 用户助手关联 ID
 * @returns {Promise<{
 *   code: string,
 *   message: string,
 *   data?: Array<{
 *     id: number|string,
 *     title: string,
 *     userId: number|string,
 *     userAssistantId: number|string
 *   }>,
 *   timestamp?: number
 * }>}
 */
export function getTopicsByUserAssistantId(userAssistantId) {
  return request('/api/user-message-sessions/user-assistant/' + encodeURIComponent(userAssistantId), {
    method: 'GET'
  })
}

/**
 * 获取已登录用户的助手列表
 * GET /api/user-assistants/user/{userId}
 * @param {string|number} userId 用户 ID
 * @returns {Promise<{
 *   code: string,
 *   message: string,
 *   data?: Array<{
 *     id: number|string,
 *     name: string,
 *     userId: number|string,
 *     emoji: string,
 *     prompt: string,
 *     llmCode: string
 *   }>,
 *   timestamp?: number
 * }>}
 */
export function getUserAssistantsByUserId(userId) {
  return request('/api/user-assistants/user/' + encodeURIComponent(userId), {
    method: 'GET'
  })
}

/**
 * 删除某个 user-assistant 下的助手话题
 * DELETE /api/user-message-sessions/user-assistant/{userAssistantId}
 * @param {string|number} userAssistantId 用户助手关联 ID
 * @returns {Promise<{code: string, message: string, data?: any}>}
 */
export function deleteTopicByUserAssistantId(userAssistantId) {
  return request('/api/user-message-sessions/user-assistant/' + encodeURIComponent(userAssistantId), {
    method: 'DELETE'
  })
}

/**
 * 获取某个会话（话题）下的聊天记录
 * GET /api/user-chat-memories/session/{sessionId}
 * @param {string|number} sessionId 会话 ID（话题的 sessionId）
 * @returns {Promise<{
 *   code: string,
 *   message: string,
 *   data?: Array<{
 *     id: number,
 *     conversationId: number,
 *     timestamp: string,
 *     type: 'USER' | 'ASSISTANT',
 *     content: string
 *   }>,
 *   timestamp?: number
 * }>}
 */
export function getChatMemoriesBySessionId(sessionId) {
  return request('/api/user-chat-memories/session/' + encodeURIComponent(sessionId), {
    method: 'GET'
  })
}
